"use client"

import Image from "next/image"
import { Button } from "@/components/ui/button"
import { useLanguage } from "@/contexts/language-context"
import { useAuth } from "@/contexts/auth-context"
import { useEffect, useState } from 'react'
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { GetStartedButton } from '@/components/get-started-button'

/**
 * 顶部展示型 Hero，不包含交互生成逻辑。
 * 保留现有配色（蓝 / 靛），右侧放一个占位多层卡片示意未来 AI 回复效果。
 * 原有带输入的 Hero 将逐步下沉为 LiveDemoSection（后续迭代）。
 */
export function HeroShowcase() {
  const { t } = useLanguage()
  const { isAuthenticated } = useAuth()
  const [stats, setStats] = useState<{users:number;replies:number;languages:number}|null>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  useEffect(() => {
    let ignore = false
    const load = async () => {
      try {
        setLoadingStats(true)
        const res = await fetch('/api/stats', { next: { revalidate: 300 } })
        if (!res.ok) return
        const j = await res.json()
        if (!ignore) setStats({ users: j.users, replies: j.replies, languages: j.languages })
      } finally { setLoadingStats(false) }
    }
    load()
    const id = setInterval(load, 300000) // 5min refresh
    return () => { ignore = true; clearInterval(id) }
  }, [])

  // 登录后隐藏按钮，不需要跳转逻辑

  return (
    <section className="relative overflow-hidden pt-28 pb-24 md:pt-32 bg-gradient-to-b from-transparent via-white/90 to-white dark:from-transparent dark:via-zinc-950 dark:to-zinc-900/90">
      {/* soft top fade to blend with preceding hero */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 inset-x-0 h-32 bg-gradient-to-b from-white/40 dark:from-zinc-900/40 to-transparent" />
        <div className="absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-blue-200/25 blur-3xl dark:bg-indigo-700/25" />
        <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-indigo-300/25 blur-3xl dark:bg-blue-800/25" />
      </div>
  <div className="relative z-10 mx-auto w-full max-w-screen-2xl px-6 md:px-12 flex flex-col-reverse lg:flex-row items-center gap-20">
  <div className="relative w-full max-w-xl lg:max-w-lg xl:max-w-xl">
          <div className="relative h-[380px]">
            <div className={cn("absolute inset-0 rounded-3xl border border-blue-200/60 dark:border-indigo-700/40 bg-gradient-to-br from-white via-blue-50 to-blue-100 dark:from-zinc-800 dark:via-zinc-800 dark:to-indigo-900/30 shadow-2xl p-6 flex flex-col")}>              
              <div className="text-sm font-medium text-blue-700 dark:text-indigo-200 mb-2">{t('hero.preview.aiReply') || 'AI Reply Preview'}</div>
              <div className="space-y-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                <SkeletonLine w="90%" />
                <SkeletonLine w="75%" />
                <SkeletonLine w="60%" />
                <div className="pt-2">
                  <SkeletonLine w="40%" />
                </div>
              </div>
              <div className="mt-auto flex gap-2 pt-6">
                <div className="h-9 flex-1 rounded-full bg-blue-100/90 dark:bg-indigo-800/60 text-white text-xs flex items-center justify-center">{t('hero.preview.primary') || 'Polish Tone'}</div>
                <div className="h-9 flex-1 rounded-full bg-blue-100 dark:bg-indigo-800/60 text-blue-700 dark:text-indigo-200 text-xs flex items-center justify-center">{t('hero.preview.secondary') || 'Translate'}</div>
              </div>
            </div>
            <div className="absolute -right-6 -bottom-8 w-40 h-40 rounded-2xl bg-white/70 dark:bg-zinc-800/80 border border-blue-200/50 dark:border-indigo-700/40 shadow-xl backdrop-blur-md p-4 flex flex-col animate-[float_6s_ease-in-out_infinite]">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-indigo-300">{t('hero.preview.scenarioLabel') || 'Scenario'}</span>
              <span className="mt-1 text-xs text-gray-600 dark:text-gray-200">{t('scenario.customerService') || 'Customer Support'}</span>
              <div className="mt-4 space-y-1">
                <SmallPill text={t('hero.preview.tone') || 'Friendly'} />
                <SmallPill text={t('hero.preview.target') || 'Client'} />
              </div>
            </div>
          </div>
        </div>
  <div className="w-full max-w-2xl text-center lg:text-left">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/70 dark:border-indigo-700/50 bg-blue-50 dark:bg-indigo-900/40 px-4 py-1.5 mb-6 text-sm font-medium text-blue-700 dark:text-indigo-200">
            {t('features.badge') || 'AI Communication'}
          </div>
          <h2 className="text-4xl md:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-[1.1]">
            {t('hero.headline') || 'Craft Clear & Impactful Communication in Seconds'}
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('hero.subheadline') || 'Transform rough ideas into polished responses with scenario, tone & target intelligence—powered by your existing workflow.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <GetStartedButton className="h-12 px-7 text-base bg-blue-600 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 shadow-lg shadow-blue-600/25 dark:shadow-indigo-600/25" />
            {isAuthenticated ? null : (
              <Button variant="outline" onClick={() => {
                const el = document.querySelector('#features')
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }} className="h-12 px-7 text-base border-blue-300 dark:border-indigo-600/60 bg-white/70 dark:bg-zinc-800/70 backdrop-blur-sm">
                {t('hero.cta.learnMore') || 'Learn More'}
              </Button>
            )}
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 text-center lg:text-left">
            <StatBlock label={t('stats.users')||'Users'} value={formatNumber(stats?.users, '2.3K+')} loading={loadingStats} />
            <StatBlock label={t('stats.replies')||'Replies'} value={formatNumber(stats?.replies, '180K+')} loading={loadingStats} />
            <StatBlock label={t('stats.languages')||'Languages'} value={stats? stats.languages + '+' : '18+'} loading={loadingStats} />
          </div>
        </div>
      </div>
    </section>
  )
}

function SkeletonLine({ w }: { w: string }) {
  return <div className="h-3 rounded bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 dark:from-indigo-800 dark:via-indigo-700 dark:to-indigo-800 animate-pulse" style={{ width: w }} />
}

function SmallPill({ text }: { text: string }) {
  return <div className="inline-flex items-center rounded-full bg-blue-600/30 dark:bg-indigo-500/80 text-white px-2 py-1 text-[10px] font-medium shadow">{text}</div>
}

function formatNumber(n: number | undefined, fallback: string) {
  if (n === undefined) return fallback
  if (n >= 1_000_000) return (n/1_000_000).toFixed(1).replace(/\.0$/,'') + 'M+'
  if (n >= 1_000) return (n/1_000).toFixed(1).replace(/\.0$/,'') + 'K+'
  return n + '+'
}

function StatBlock({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
        {loading ? <span className="inline-block h-5 w-14 rounded bg-gray-200 dark:bg-zinc-700 animate-pulse"/> : value}
      </span>
      <span className="text-[10px] tracking-wide font-medium text-gray-500 dark:text-gray-400 uppercase">{label}</span>
    </div>
  )
}
